const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

export const SEASON_PRESETS: { label: string; months: number[] }[] = [
  { label: 'Spring', months: [3, 4, 5] },
  { label: 'Summer', months: [6, 7, 8] },
  { label: 'Fall', months: [9, 10, 11] },
  { label: 'Winter', months: [12, 1, 2] },
]

/**
 * Collapses a set of available months into human-readable ranges, e.g.
 * [4,5,6,9,10] -> "Apr-Jun, Sep-Oct". Handles a single range that wraps
 * across the year boundary (e.g. [11,12,1,2] -> "Nov-Feb"), but multiple
 * separate windows are always shown individually (arugula/broccoli case).
 */
export function formatMonthRanges(months: number[] | null | undefined): string {
  if (!months || months.length === 0) return 'Not set'
  const unique = Array.from(new Set(months)).sort((a, b) => a - b)
  if (unique.length === 12) return 'Year-round'

  // If the selection wraps the year boundary (both December-ish and
  // January-ish months present with a gap elsewhere), rotate so the run
  // starting right after the largest gap comes first -- this turns e.g.
  // [1,2,11,12] into the single range Nov-Dec-Jan-Feb instead of splitting
  // it into two ranges at the Dec/Jan boundary.
  let rotated = unique
  let largestGap = -1
  let gapStartIdx = -1
  for (let i = 0; i < unique.length; i++) {
    const cur = unique[i]
    const next = unique[(i + 1) % unique.length]
    const gap = next > cur ? next - cur : next + 12 - cur
    if (gap > largestGap) {
      largestGap = gap
      gapStartIdx = (i + 1) % unique.length
    }
  }
  if (gapStartIdx > 0) {
    rotated = [...unique.slice(gapStartIdx), ...unique.slice(0, gapStartIdx)]
  }

  const ranges: string[] = []
  let start = rotated[0]
  let prev = rotated[0]
  for (let i = 1; i <= rotated.length; i++) {
    const cur = rotated[i]
    const isConsecutive = cur !== undefined && (cur === (prev % 12) + 1)
    if (!isConsecutive) {
      ranges.push(start === prev ? MONTH_NAMES[start - 1] : `${MONTH_NAMES[start - 1]}-${MONTH_NAMES[prev - 1]}`)
      start = cur
    }
    prev = cur
  }
  return ranges.join(', ')
}

/**
 * Whether an ingredient has a real season: some months saved, but not all twelve. This is what "seasonal"
 * versus "year-round" means for admins. Do NOT use the ingredient's isSeasonal flag for that: the weekly
 * and monthly job sets it to "in season this month", so every ingredient out of season reads false.
 */
export function hasSeasonalWindow(months: number[] | null | undefined): boolean {
  const unique = new Set(months ?? [])
  return unique.size > 0 && unique.size < 12
}

/** Whether today's month is in the given set of available months. */
export function isInSeasonNow(months: number[] | null | undefined): boolean {
  if (!months || months.length === 0) return false
  const currentMonth = new Date().getMonth() + 1
  return months.includes(currentMonth)
}
